import * as React from 'react';
import { Checkbox, Input } from '@fluentui/react-components';
import { FUTURE_EVENTS, WEEKEND_NOTE, type EventKind, type IProgram } from '../../api/src/programs';
import { asksReceived, countedFrom, deadlinesFor, type IDeadlineLine, type IEventInput } from '../records/events';

/** Whether what's entered is enough to record the event (a date, and none in the future unless the event is ahead). */
export function eventInputReady(kind: EventKind, input: IEventInput, today: string): boolean {
  const from = countedFrom(kind, input);
  if (!from) {
    return false;
  }
  const future: boolean = FUTURE_EVENTS.indexOf(kind) !== -1;
  return future || ![input.letterDate, asksReceived(kind) ? input.receivedDate : undefined].some(d => d && d > today);
}

/**
 * The dates of a letter or event, and the deadlines that follow, for a person to check as they enter
 * them: decisions and reasons ask for the date on the letter and the date received; letters that
 * usually state a due date ask for it first; the other period (usually longer) is used only when staff tick it.
 */
export function EventDates(props: { kind: EventKind; input: IEventInput; onChange: (input: IEventInput) => void; letterLabel?: string }): React.ReactElement {
  const { kind, input } = props;
  const future: boolean = FUTURE_EVENTS.indexOf(kind) !== -1;
  const set = (patch: Partial<IEventInput>): void => props.onChange({ ...input, ...patch });
  return (
    <>
      <label>{props.letterLabel ?? (asksReceived(kind) ? 'Date on the letter' : future ? 'Date' : 'Date on the letter (or of the event)')}
        <Input type="date" value={input.letterDate ?? ''} onChange={(_, x) => set({ letterDate: x.value })} />
      </label>
      {asksReceived(kind) && (
        <label>Date received
          <Input type="date" value={input.receivedDate ?? ''} onChange={(_, x) => set({ receivedDate: x.value })} />
        </label>
      )}
    </>
  );
}

export function DeadlinePreview(props: { program: IProgram; kind: EventKind; input: IEventInput; onChange: (input: IEventInput) => void }): React.ReactElement | null {
  const { kind, input } = props;
  if (!countedFrom(kind, input)) {
    return asksReceived(kind) ? <span className="small">Enter the date on the letter, the date it was received, or both. Deadlines are counted from the earlier one.</span> : null;
  }
  const lines: IDeadlineLine[] = deadlinesFor(props.program, kind, input);
  if (!lines.length) {
    return null;
  }
  const setStated = (ruleId: string, v: string): void => props.onChange({ ...input, stated: { ...input.stated, [ruleId]: v } });
  const setTicked = (ruleId: string, v: boolean): void => props.onChange({ ...input, ticked: { ...input.ticked, [ruleId]: v } });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <strong style={{ fontSize: 14 }}>Deadlines that follow (calculated, please check against the letter):</strong>
      {lines.map(l => (
        <div key={l.ruleId} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(l.asStated || l.letterFirst) && (
            <label>{l.label}: due date on the letter
              <Input type="date" value={input.stated?.[l.ruleId] ?? ''} onChange={(_, x) => setStated(l.ruleId, x.value)} />
            </label>
          )}
          {l.asStated && <span className="small">Enter the date the letter gives (usually {l.asStated.min} to {l.asStated.max} days).</span>}
          {l.due && <span>{l.label}: <strong>{l.due}</strong>{l.calculated ? ' (calculated)' : ' (from the letter)'}</span>}
          {l.letterFirst && l.calculated && <span className="small">No due date from the letter is entered, so this date is counted instead. Enter the letter's own date above if it gives one.</span>}
          {l.tick && <Checkbox label={l.tick} checked={l.ticked} onChange={(_, x) => setTicked(l.ruleId, !!x.checked)} />}
          {l.calculated && <span className="small">{WEEKEND_NOTE}</span>}
          <span className="small">{l.source}</span>
        </div>
      ))}
    </div>
  );
}
